import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Upload, FileText, Headphones, Video, X, ArrowLeft, Lock, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getAuthHeaders, authFetch } from '../utils/authUtils';
import GumroadStyleUpload from '../components/GumroadStyleUpload';
import EpisodeStatsChip from '../components/EpisodeStatsChip';
import CoverImagePicker from '../components/CoverImagePicker';
import ReadingLevelSelect from '../components/ReadingLevelSelect';
import OriginalContentAck from '../components/OriginalContentAck';
import TTSGenerator from '../components/TTSGenerator';
import VoiceSelectionModal from '../components/VoiceSelectionModal';
import { submitAndAwaitTts } from '../utils/ttsPolling';
import { recordOriginalAck } from '../utils/originalAck';

const UploadContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  // Per-upload OCR mode for image/scanned-PDF uploads. 'auto' = sync extract
  // first, OCR fallback for scanned PDFs (Gemini Nano Banana). 'direct' = skip
  // OCR entirely so the file stays as a picture-book / download. Only the
  // chooser UI is conditional — the field is sent on every upload.
  const [ocrMode, setOcrMode] = useState('auto');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [role, setRole] = useState(null);

  // Metadata fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [author, setAuthor] = useState('');
  const [isEarlyAccess, setIsEarlyAccess] = useState(false);
  const [isVault, setIsVault] = useState(
    new URLSearchParams(location.search).get('vault') === '1',
  );
  const [vaultPrice, setVaultPrice] = useState(''); // empty string = free
  // General library price (£). Separate from `vaultPrice` because the vault
  // uses GBP-direct-charge while regular library content uses coin pricing
  // (£0.03 / coin → £15 cap = 500 coins for writers, no cap for admins
  // when "no-max" is on). Set in this form, then PATCHed to
  // /api/content-pricing/{id} after the upload succeeds and we have the
  // backend-issued `content_id`. Empty string ⇒ leave the item free.
  const [priceGbp, setPriceGbp] = useState('');
  const [noMaxPrice, setNoMaxPrice] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  // Reading-level tag — optional. Stored on `ai_reading_level` so the existing
  // Library filter chips already understand it.
  const [readingLevel, setReadingLevel] = useState('');
  
  // Text upload fields
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  // After a successful text upload we keep the returned content_id around
  // so the admin TTS generator below can attach `audio_url` to the same
  // record (Feb 26 2026 — TTS spec).
  const [lastTextContentId, setLastTextContentId] = useState('');

  // Required originality acknowledgement — gates every upload path on
  // this page. Reset to false after every successful upload so the next
  // submission has to re-confirm.
  const [originalAck, setOriginalAck] = useState(false);

  // Voice selection state for the text-upload publish flow. On click of
  // "Publish text", the modal opens; once a voice is picked, we upload
  // the text AND immediately call /api/tts/generate to produce the audio
  // (the URL gets pinned onto the same content record server-side).
  const [textVoiceModalOpen, setTextVoiceModalOpen] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  // Resolve current user role (only admins see the Vault checkbox).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/auth/me`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setRole(data.role || null);
          if (data.role !== 'admin') setIsVault(false);
        }
      } catch {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [BACKEND_URL]);
  
  const GENRES = [
    'Fiction', 'Non-Fiction', 'Science Fiction', 'Fantasy', 'Mystery', 
    'Thriller', 'Romance', 'Poetry', 'Biography', 'History', 'Science',
    'Self-Help', 'Business', 'Technology', 'Education', 'Other'
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large! Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
        event.target.value = '';
        return;
      }
      setSelectedFile(file);
      // Default OCR mode based on file type:
      //  - PDFs: "auto" — fast text-layer extract first, OCR only when needed.
      //  - Images (PNG/JPG/JPEG): "direct" — most users want a picture book.
      //  - Everything else: "auto" (no-op for text formats).
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) {
        setOcrMode('direct');
      } else {
        setOcrMode('auto');
      }
      // Auto-fill title from filename
      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, ''));  // Remove extension
      }
    }
  };

  const runOcrPreview = async () => {
    if (!selectedFile || previewLoading) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      const sessionToken = localStorage.getItem('session_token');
      const headers = sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
      const res = await fetch(`${BACKEND_URL}/api/upload/ocr-preview`, {
        method: 'POST',
        headers,
        body: fd,
        ...(sessionToken ? {} : { credentials: 'include' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setPreviewData({ error: err.detail || `Preview failed (HTTP ${res.status})` });
        return;
      }
      setPreviewData(await res.json());
    } catch (e) {
      setPreviewData({ error: e.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileUpload = async (contentType) => {
    // Hard re-entry guard — stops accidental double-taps, network retries,
    // and React strict-mode double invocations from producing duplicate records.
    if (uploading) return;
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    if (!title) {
      alert('Please enter a title');
      return;
    }

    if (!originalAck) {
      alert('Please confirm the originality acknowledgement before uploading.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('genre', genre || 'Uncategorized');
      formData.append('author', author || 'Unknown');
      formData.append('is_early_access', isEarlyAccess ? 'true' : 'false');
      // Vault flag only honored server-side when the caller is admin AND
      // the content type is ebook or audiobook.
      formData.append(
        'is_vault',
        isVault && role === 'admin' && (contentType === 'ebook' || contentType === 'audiobook')
          ? 'true'
          : 'false',
      );
      // Price (GBP) — only honored for admin vault uploads of ebook/audiobook.
      const priceNum = parseFloat(vaultPrice);
      formData.append(
        'price_amount',
        isVault && role === 'admin' && !Number.isNaN(priceNum) && priceNum > 0
          ? priceNum.toFixed(2)
          : '0',
      );
      if (coverImageUrl) {
        formData.append('cover_image_url', coverImageUrl);
      }
      // OCR mode is only meaningful for ebook image / PDF uploads but the
      // backend ignores it for other content types so we always send.
      formData.append('ocr_mode', ocrMode || 'auto');
      if (readingLevel) {
        formData.append('reading_level', readingLevel);
      }

      // Get auth headers (Authorization header only, FormData handles Content-Type)
      const sessionToken = localStorage.getItem('session_token');
      const headers = sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};

      const response = await fetch(`${BACKEND_URL}/api/upload/${contentType}`, {
        method: 'POST',
        headers: headers,
        body: formData,
        ...(sessionToken ? {} : { credentials: 'include' })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      setUploadProgress(100);

      // Stamp the originality acknowledgement onto the new content row
      // (best-effort; the FE checkbox is the hard gate).
      if (data?.content_id) {
        recordOriginalAck(data.content_id).catch(() => {});
      }

      // 💰 If a price was set on the form, PATCH the content's coin price
      // immediately so it's live the moment moderation approves it.
      //   GBP → coins conversion: £0.03 / coin (matches backend constant
      //   in `routes/pricing_routes.py::DEFAULT_MAX_PRICE_GBP/COINS`).
      // Capped at £15 (500 coins) for scribes unless they checked "Remove
      // max" (admin-only toggle on the form).
      const libraryPriceNum = parseFloat(priceGbp);
      if (!Number.isNaN(libraryPriceNum) && libraryPriceNum > 0 && data?.content_id) {
        try {
          const coins = Math.max(1, Math.round(libraryPriceNum / 0.03));
          const priceRes = await authFetch(
            `${BACKEND_URL}/api/content-pricing/${encodeURIComponent(data.content_id)}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                price_coins: coins,
                no_max_price: role === 'admin' ? !!noMaxPrice : false,
              }),
            },
          );
          if (!priceRes.ok) {
            const err = await priceRes.json().catch(() => ({}));
            // Surface the price error but don't roll back the upload —
            // the user can re-edit the price later via Manage Content.
            alert(`⚠️ Uploaded, but price couldn't be set: ${err.detail || priceRes.status}`);
          }
        } catch (e) {
          alert(`⚠️ Uploaded, but price couldn't be set: ${e.message}`);
        }
      }

      alert(`✅ ${data.message}\n\nTitle: ${data.title}\nSize: ${data.file_size}\nStatus: ${data.status}`);
      
      // Clear form
      setSelectedFile(null);
      setTitle('');
      setDescription('');
      setGenre('');
      setAuthor('');
      setOriginalAck(false);
      
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      console.error('Upload error:', error);
      alert(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleTextUpload = async (chosenVoice = null) => {
    if (!textTitle || !textContent) {
      alert('Please enter both title and text');
      return;
    }
    if (!originalAck) {
      alert('Please confirm the originality acknowledgement before uploading.');
      return;
    }
    // If no voice provided and the scribe hasn't explicitly opted out
    // (via "Publish text only" path), open the voice picker so the
    // upload and TTS happen as a single, deliberate action.
    if (chosenVoice === null) {
      setTextVoiceModalOpen(true);
      return;
    }

    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const sessionToken = localStorage.getItem('session_token');

      const response = await fetch(`${BACKEND_URL}/api/upload/text`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          title: textTitle,
          text: textContent,
          // Carry the optional level tag so text uploads land in the
          // matching Library reading-level filter immediately.
          ...(readingLevel ? { reading_level: readingLevel } : {}),
        }),
        ...(sessionToken ? {} : { credentials: 'include' })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      if (data?.content_id) {
        recordOriginalAck(data.content_id).catch(() => {});
        setLastTextContentId(data.content_id);
      }

      // Auto-synthesize the audio if a voice was picked. Best-effort —
      // text upload succeeded already, so a TTS failure is a soft warn,
      // not a hard error. submitAndAwaitTts() handles the async job
      // pipeline + polling, and the proxy pins `audio_url` onto the
      // content record once the job finishes.
      if (chosenVoice && chosenVoice !== '__text_only__' && data?.content_id) {
        try {
          const finalUrl = await submitAndAwaitTts({
            text: textContent,
            voice: chosenVoice,
            language: 'en',
            contentId: data.content_id,
          });
          alert(
            `✅ Text + audio published!\n\nTitle: ${data.title}\n` +
            `Words: ${data.word_count}\nVoice: ${chosenVoice}\n\n${finalUrl}`,
          );
        } catch (ttsErr) {
          alert(
            `✅ Text uploaded.\n\n⚠️ Audio narration failed: ${ttsErr.message || ttsErr}.\n` +
            `Your text is published; retry TTS from the Generator panel.`,
          );
        }
      } else {
        alert(`✅ Text uploaded!\n\nTitle: ${data.title}\nWords: ${data.word_count}`);
      }

      // Keep textContent + textTitle around so the admin can immediately
      // run TTS on the same body without re-typing — only clear on
      // navigate-away. (Spec says: generate audio after the upload.)
      setOriginalAck(false);
      setTextVoiceModalOpen(false);
      // No more auto-navigate so the admin can use the TTS generator.
    } catch (error) {
      console.error('Upload error:', error);
      alert(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-white mb-4">
            ← Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-white mb-2">Upload Content</h1>
          <p className="text-gray-400">Upload ebooks, audiobooks, videos, or text to your library</p>
        </div>

        {/* Cover image picker — applies to whichever tab is submitted */}
        <div className="p-4 mb-4 rounded-xl border border-slate-700 bg-slate-900/40">
          <CoverImagePicker value={coverImageUrl} onChange={setCoverImageUrl} />
        </div>

        {/* Global Early-Access toggle — applies to whichever tab the user submits from */}
        <label
          className="flex items-center gap-3 p-3 mb-5 rounded-xl border border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-900/20 via-purple-900/15 to-indigo-900/20 cursor-pointer select-none"
          data-testid="upload-early-access-toggle"
        >
          <input
            type="checkbox"
            checked={isEarlyAccess}
            onChange={(e) => setIsEarlyAccess(e.target.checked)}
            className="w-4 h-4 accent-fuchsia-500"
          />
          <div className="flex-1">
            <p className="text-sm font-semibold text-purple-100">
              Mark as Early Access <span className="text-[10px] uppercase tracking-wider text-fuchsia-300 ml-1">Top Tier only</span>
            </p>
            <p className="text-xs text-purple-200/80 mt-0.5">
              Only Top Tier subscribers (and admins) will see this item in their library until you flip the flag off.
            </p>
          </div>
        </label>

        {/* Admin-only Secret Vault toggle. Honored only for ebook/audiobook tabs. */}
        {role === 'admin' && (
          <label
            className={`flex items-center gap-3 p-3 mb-3 rounded-xl border cursor-pointer select-none ${
              isVault
                ? 'border-fuchsia-500 bg-fuchsia-900/30 shadow-[0_0_30px_-10px_rgba(217,70,239,0.6)]'
                : 'border-slate-700 bg-slate-800/50'
            }`}
            data-testid="upload-vault-toggle"
          >
            <input
              type="checkbox"
              checked={isVault}
              onChange={(e) => setIsVault(e.target.checked)}
              className="w-4 h-4 accent-fuchsia-500"
            />
            <Lock className="w-4 h-4 text-fuchsia-300 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">
                Save into Secret Vault
                <span className="text-[10px] uppercase tracking-wider text-fuchsia-300 ml-2">Admin only</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Hides this item from the regular Library. Only users with Vault access + their code can see it. Applies to <span className="text-fuchsia-300">Ebook</span>, <span className="text-fuchsia-300">Audiobook</span>, and <span className="text-fuchsia-300">Cinematic</span> uploads.
              </p>
            </div>
          </label>
        )}

        {/* General library price — scribes + admins set a coin price on any
            ebook/audiobook upload. Hidden when "Vault" is on because vault
            uses a separate GBP charge below. £15 cap enforced server-side
            (500 coins @ £0.03); admins can flip "Remove max" for higher. */}
        {(role === 'writer' || role === 'admin') && !isVault && (
          <div
            className="p-4 mb-5 rounded-xl border border-amber-500/30 bg-slate-900/40"
            data-testid="library-price-section"
          >
            <p className="text-sm font-semibold text-white mb-2">
              Price (GBP £) <span className="text-[10px] uppercase tracking-wider text-amber-300 ml-1">Library item</span>
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPriceGbp('')}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  priceGbp === '' || parseFloat(priceGbp) === 0
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-800 text-gray-300 border border-slate-700'
                }`}
                data-testid="library-price-free-btn"
              >
                Free
              </button>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={role === 'admin' && noMaxPrice ? undefined : 15}
                  value={priceGbp}
                  onChange={(e) => setPriceGbp(e.target.value)}
                  placeholder="e.g. 4.99"
                  className="w-full pl-7 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-400"
                  data-testid="library-price-input"
                />
              </div>
            </div>
            {/* Quick presets — saves typing for the most common price points. */}
            <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="library-price-presets">
              {[2.99, 4.99, 9.99, 14.99].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriceGbp(String(p))}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-md border transition-colors ${
                    parseFloat(priceGbp) === p
                      ? 'border-amber-400 bg-amber-500/20 text-amber-100'
                      : 'border-slate-700 bg-slate-800/60 text-slate-300 hover:border-slate-500'
                  }`}
                  data-testid={`library-price-preset-${String(p).replace('.', '_')}`}
                >
                  £{p}
                </button>
              ))}
            </div>
            {role === 'admin' && (
              <label
                className="inline-flex items-center gap-2 mt-3 text-xs font-semibold text-rose-200 cursor-pointer select-none"
                data-testid="library-price-no-max-label"
              >
                <input
                  type="checkbox"
                  checked={noMaxPrice}
                  onChange={(e) => setNoMaxPrice(e.target.checked)}
                  className="accent-rose-500"
                  data-testid="library-price-no-max-checkbox"
                />
                Remove £15 max <span className="text-rose-400/70 font-normal">(admin only)</span>
              </label>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Writers capped at <span className="text-amber-200 font-semibold">£15</span> (500 coins). Saved at <span className="text-amber-200 font-semibold">£0.03 / coin</span> server-side. Leave at 0 / click "Free" to publish without a paywall.
            </p>
          </div>
        )}

        {/* Price field — only visible when vault is checked */}
        {role === 'admin' && isVault && (
          <div
            className="p-4 mb-5 rounded-xl border border-fuchsia-500/30 bg-slate-900/40"
            data-testid="vault-price-section"
          >
            <p className="text-sm font-semibold text-white mb-2">
              Price (GBP £) <span className="text-[10px] uppercase tracking-wider text-fuchsia-300 ml-1">Vault item</span>
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setVaultPrice('')}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  vaultPrice === '' || parseFloat(vaultPrice) === 0
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-800 text-gray-300 border border-slate-700'
                }`}
                data-testid="vault-price-free-btn"
              >
                Free
              </button>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={vaultPrice}
                  onChange={(e) => setVaultPrice(e.target.value)}
                  placeholder="e.g. 4.99"
                  className="w-full pl-7 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500"
                  data-testid="vault-price-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Single GBP price for everyone. Leave at 0 / click "Free" for no charge. Payment integration ships in the next update — for now the price is captured & displayed only.
            </p>
          </div>
        )}

        <Tabs defaultValue="text" className="w-full">
          <TabsList
            className="bg-transparent border-0 p-0 grid grid-cols-2 gap-3 h-auto"
            data-testid="upload-type-tabs"
          >
            <TabsTrigger
              value="text"
              data-testid="upload-tab-text"
              className="group relative flex flex-col items-center justify-center gap-2 h-28 sm:h-32 rounded-2xl border-2 border-amber-400/30 bg-gradient-to-br from-amber-500/20 via-orange-600/15 to-rose-700/15 hover:from-amber-500/35 hover:via-orange-600/25 hover:to-rose-700/25 transition-all data-[state=active]:from-amber-500/60 data-[state=active]:via-orange-600/55 data-[state=active]:to-rose-700/55 data-[state=active]:border-amber-300 data-[state=active]:shadow-[0_0_50px_-15px_rgba(251,191,36,0.65)] data-[state=active]:scale-[1.03]"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-lg group-data-[state=active]:scale-110 transition-transform">
                <FileText className="w-7 h-7 text-white" strokeWidth={2.2} />
              </div>
              <span className="text-sm font-bold text-white">Text</span>
              <span className="text-[10px] uppercase tracking-wider text-amber-200/80 -mt-1">Quick note</span>
            </TabsTrigger>

            <TabsTrigger
              value="ebook"
              data-testid="upload-tab-ebook"
              className="group relative flex flex-col items-center justify-center gap-2 h-28 sm:h-32 rounded-2xl border-2 border-indigo-400/30 bg-gradient-to-br from-indigo-500/20 via-blue-600/15 to-sky-700/15 hover:from-indigo-500/35 hover:via-blue-600/25 hover:to-sky-700/25 transition-all data-[state=active]:from-indigo-500/60 data-[state=active]:via-blue-600/55 data-[state=active]:to-sky-700/55 data-[state=active]:border-indigo-300 data-[state=active]:shadow-[0_0_50px_-15px_rgba(99,102,241,0.65)] data-[state=active]:scale-[1.03]"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-blue-600 flex items-center justify-center shadow-lg group-data-[state=active]:scale-110 transition-transform">
                <BookOpen className="w-7 h-7 text-white" strokeWidth={2.2} />
              </div>
              <span className="text-sm font-bold text-white">Ebook</span>
              <span className="text-[10px] uppercase tracking-wider text-indigo-200/80 -mt-1">PDF · EPUB · TXT</span>
            </TabsTrigger>

            {/* Audiobook + Cinematic upload tiles intentionally removed
                Feb 28 2026 per user instruction: "Scrub everything about
                upload for audio and cinema — leave it empty and wait for
                new instructions". The legacy routes
                /upload-audiobook-series + /upload-cinematic-series
                remain wired but render empty placeholders, and the
                backend (audiobook_series + cinematic_series) is left
                intact so previously-uploaded content still plays back.
            */}
          </TabsList>

          {/* Text Upload */}
          <TabsContent value="text" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Upload Text Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder="Title"
                  value={textTitle}
                  onChange={(e) => setTextTitle(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 text-white"
                />
                <Textarea
                  placeholder="Paste your text here..."
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  rows={12}
                  className="bg-slate-900/50 border-slate-700 text-white"
                  data-testid="upload-text-content-textarea"
                />
                {/* Live readability preview — words · min read · flash
                    · level chips. Same formulas readers see (iter 68). */}
                <EpisodeStatsChip text={textContent} className="-mt-1" />
                <ReadingLevelSelect value={readingLevel} onChange={setReadingLevel} />
                <OriginalContentAck
                  checked={originalAck}
                  onChange={setOriginalAck}
                  testId="upload-text-original-ack"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleTextUpload()}
                    disabled={uploading || !originalAck}
                    className="bg-fuchsia-600 hover:bg-fuchsia-500"
                    data-testid="upload-text-publish-btn"
                  >
                    {uploading ? 'Publishing…' : 'Publish with TTS voice'}
                  </Button>
                  <Button
                    onClick={() => handleTextUpload('__text_only__')}
                    disabled={uploading || !originalAck}
                    variant="outline"
                    className="border-slate-600 text-slate-200 hover:bg-slate-800"
                    data-testid="upload-text-publish-text-only-btn"
                  >
                    {uploading ? 'Publishing…' : 'Publish text only'}
                  </Button>
                </div>

                <VoiceSelectionModal
                  open={textVoiceModalOpen}
                  onCancel={() => setTextVoiceModalOpen(false)}
                  onConfirm={(v) => handleTextUpload(v)}
                  busy={uploading}
                />

                {/* Admin-only TTS — text-to-audio generation. Hides for
                    non-admins; lights up the moment there's text in the
                    box, and once an upload finishes we hand it the
                    content_id so the generated audio_url gets pinned
                    onto the same record. */}
                {(role === 'admin' || role === 'coadmin' || role === 'king') && (
                  <TTSGenerator
                    text={textContent}
                    contentId={lastTextContentId}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ebook Upload */}
          <TabsContent value="ebook" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Upload Ebook</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Metadata Fields */}
                <div className="space-y-3 pb-4 border-b border-slate-700">
                  <Input
                    placeholder="Title *"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="bg-slate-900/50 border-slate-700 text-white"
                    required
                  />
                  <Textarea
                    placeholder="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={genre}
                      onChange={(e) => setGenre(e.target.value)}
                      className="bg-slate-900/50 border border-slate-700 text-white rounded-md px-3 py-2"
                    >
                      <option value="">Select Genre</option>
                      {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <Input
                      placeholder="Author"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>
                  <ReadingLevelSelect value={readingLevel} onChange={setReadingLevel} />
                </div>
                
                {/* File Upload */}
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <Input
                    type="file"
                    accept=".pdf,.epub,.txt,.doc,.docx,.mobi,.png,.jpg,.jpeg"
                    onChange={handleFileSelect}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                  {selectedFile && (
                    <p className="text-green-400 mt-2">✓ {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)</p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">Supported: PDF, EPUB, TXT, DOC, DOCX, MOBI, PNG, JPG (Max 100MB)</p>
                </div>

                {/* OCR / Direct chooser — only show for image + PDF ebook uploads. */}
                {selectedFile && (() => {
                  const ext = (selectedFile.name.split('.').pop() || '').toLowerCase();
                  if (!['png', 'jpg', 'jpeg', 'webp', 'gif', 'pdf'].includes(ext)) return null;
                  const isImage = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
                  return (
                    <div
                      className="rounded-lg border border-fuchsia-500/30 bg-slate-900/40 p-3 space-y-2"
                      data-testid="ocr-mode-chooser"
                    >
                      <p className="text-sm font-semibold text-white flex items-center gap-2">
                        <span className="text-fuchsia-300">⚡ How should we process this {isImage ? 'image' : 'PDF'}?</span>
                      </p>
                      <label
                        className={`flex items-start gap-3 p-2.5 rounded-md cursor-pointer transition-colors ${
                          ocrMode === 'direct'
                            ? 'bg-indigo-500/15 border border-indigo-500/40'
                            : 'bg-slate-800/40 border border-slate-700/50 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="ocr-mode"
                          value="direct"
                          checked={ocrMode === 'direct'}
                          onChange={() => setOcrMode('direct')}
                          className="w-4 h-4 accent-indigo-500 mt-0.5"
                          data-testid="ocr-mode-direct"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white">📄 Direct upload</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {isImage
                              ? 'Display the image inline as a picture book. Fastest. No text search or TTS.'
                              : 'Keep the PDF as-is. Reader extracts the text layer if present, otherwise shows a download link.'}
                          </p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-2.5 rounded-md cursor-pointer transition-colors ${
                          ocrMode !== 'direct'
                            ? 'bg-fuchsia-500/15 border border-fuchsia-500/40'
                            : 'bg-slate-800/40 border border-slate-700/50 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="ocr-mode"
                          value="extract"
                          checked={ocrMode !== 'direct'}
                          onChange={() => setOcrMode('extract')}
                          className="w-4 h-4 accent-fuchsia-500 mt-0.5"
                          data-testid="ocr-mode-extract"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white">🔍 Extract text (OCR via Gemini)</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Convert {isImage ? 'the image' : 'each scanned page'} into searchable text. Adds ~3s per page.
                            Enables TTS, summaries, AI quizzes, and chapter navigation.
                          </p>
                          {ocrMode === 'extract' && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); runOcrPreview(); }}
                              disabled={previewLoading}
                              className="mt-2 text-[11px] text-fuchsia-300 hover:text-fuchsia-200 underline disabled:opacity-60"
                              data-testid="ocr-preview-btn"
                            >
                              {previewLoading ? 'Sampling first 2 pages...' : '👁 Preview OCR result on first 2 pages'}
                            </button>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })()}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                <OriginalContentAck
                  checked={originalAck}
                  onChange={setOriginalAck}
                  testId="upload-ebook-original-ack"
                />
                <Button
                  onClick={() => handleFileUpload('ebook')}
                  disabled={uploading || !selectedFile || !originalAck}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {uploading ? 'Uploading...' : 'Upload Ebook'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audiobook + Cinematic upload bodies removed Feb 28 2026
              per user instruction: "Scrub everything about upload for
              audio and cinema — leave it empty and wait for new
              instructions". TabsContent values "audiobook" and
              "cinematic" intentionally absent; the Tab triggers are
              also removed so they cannot be selected. Routes
              /upload-audiobook-series + /upload-cinematic-series
              still resolve but render an empty placeholder. */}
        </Tabs>
      </div>

      {/* OCR Preview Modal — shows the first 2-page OCR result before commit. */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="ocr-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                👁 OCR Preview
                {previewData?.pages_sampled > 0 && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40">
                    First {previewData.pages_sampled} {previewData.pages_sampled === 1 ? 'page' : 'pages'}
                  </span>
                )}
              </h3>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="text-gray-400 hover:text-white text-xl leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-slate-800"
                data-testid="ocr-preview-close"
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {previewLoading ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="w-10 h-10 mx-auto rounded-full border-2 border-fuchsia-500/40 border-t-fuchsia-500 animate-spin mb-3" />
                  Sampling OCR on first {(selectedFile?.name?.endsWith('.pdf')) ? '2 pages' : 'image'}...
                </div>
              ) : previewData?.error ? (
                <div className="text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded p-3" data-testid="ocr-preview-error">
                  Preview failed: {previewData.error}
                </div>
              ) : !previewData?.has_text ? (
                <div className="text-amber-200 text-sm bg-amber-500/10 border border-amber-500/30 rounded p-3" data-testid="ocr-preview-empty">
                  ⚠️ No readable text found in the sample. Switching to <strong>Direct upload</strong> is recommended — Gemini OCR found nothing worth extracting.
                </div>
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-200 leading-relaxed" data-testid="ocr-preview-text">{previewData.text}</pre>
              )}
            </div>
            <div className="flex flex-wrap gap-2 p-4 border-t border-slate-700">
              <Button
                onClick={() => {
                  setOcrMode('extract');
                  setPreviewOpen(false);
                }}
                disabled={previewLoading || !previewData?.has_text}
                className="flex-1 min-w-[140px] bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:bg-slate-600"
                data-testid="ocr-preview-keep-btn"
              >
                ✨ Keep & save with this text
              </Button>
              <Button
                onClick={() => {
                  setOcrMode('direct');
                  setPreviewOpen(false);
                }}
                disabled={previewLoading}
                variant="outline"
                className="flex-1 min-w-[140px] text-indigo-300 border-indigo-500/40 hover:bg-indigo-600/20"
                data-testid="ocr-preview-switch-direct-btn"
              >
                📄 Switch to Direct upload
              </Button>
              <Button
                onClick={() => setPreviewOpen(false)}
                disabled={previewLoading}
                variant="outline"
                className="text-gray-400 border-slate-600 hover:bg-slate-700/50"
                data-testid="ocr-preview-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadContent;